import React, { useContext, useEffect } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import AuthContext from "../../store/auth-context";
import Template from "../../resources/icons/brush.svg";
import Issued from "../../resources/icons/history-timer-icon.svg";
import WorkspaceIcon from "../../resources/icons/workspace-icon.svg";
import DraftsIcon from "../../resources/icons/folder-favorite.svg";
import FolderFavoriteDisabled from "../../resources/icons/folder-favoriteDisabled.svg";
import Settings from "../../resources/icons/setting-circular-icon.svg";
import TenantIcon from "../../resources/icons/user-round-setting.svg";
import TenantIconDisabled from "../../resources/icons/user-round-setting-disabled.svg";
import IssuedDisabled from "../../resources/icons/security-safeDisabled.svg";
import SettingsDisabled from "../../resources/icons/setting-4Disabled.svg";
import HomeIcon from "../../resources/icons/presentation-chart-icon.svg";
import ApprovalIcon from "../../resources/icons/approvals.svg";
import { GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";

const Sidebar = () => {
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await GET(EntityConstants.CURRENT_USER);
        const data = await response.json();
        authContext.setUserType(data.type?.toLowerCase() || null);
        authContext.setSidebarComponents(data.sidebar_components || []);
        if (data.tenant_code) {
          authContext.setTenantCode(data.tenant_code);
        }
        if (data.org_code) {
          authContext.setOrgCode(data.org_code);
        }
      } catch (error) {
        console.error("Error fetching current user data:", error);
      }
    };

    if (authContext.isLoggedIn) {
      fetchCurrentUser();
    }
  }, [
    authContext.isLoggedIn,
    authContext.setUserType,
    authContext.setSidebarComponents,
    authContext.setTenantCode,
    authContext.setOrgCode,
  ]);

  useEffect(() => {}, [authContext.sidebarComponents]);

  const isOrganizationPage = location.pathname.startsWith("/organizations");
  const isPasswordResetPage = location.pathname.startsWith(
    "/users/update-password"
  );
  if ((isOrganizationPage || isPasswordResetPage) && !authContext.isLoggedIn) {
    return null;
  }

  const handleWorkSpaceClick = (e) => {
    e.preventDefault();
    authContext.ProgrammeOffsetHandler(0);
    authContext.BatchOffsetHandler(0);
    localStorage.setItem("programmeOffset", 0);
    localStorage.setItem("batchOffset", 0);
    navigate("/issuer/workspace");
  };

  const handleIssuedClick = (e) => {
    e.preventDefault();
    authContext.IssuedOffsetHandler(0);
    localStorage.setItem("transactionOffset", 0);
    navigate("/issuer/transaction/all");
  };

  const handleDraftsClick = (e) => {
    e.preventDefault();
    authContext.DraftOffsetHandler(0);
    localStorage.setItem("draftOffset", 0);
    navigate("/issuer/drafts");
  };

  const handleOrganisationClick = (e) => {
    e.preventDefault();
    if (authContext.tenantCode) {
      navigate(`/issuer/tenant/${authContext.tenantCode}/organisations/users`);
    } else {
      const fetchAndNavigate = async () => {
        try {
          const response = await GET(EntityConstants.CURRENT_USER);
          const data = await response.json();
          if (data.tenant_code) {
            navigate(`/issuer/tenant/${data.tenant_code}/organisations/users`);
          } else {
            console.error("Tenant code not found");
            navigate("/issuer/tenant");
          }
        } catch (error) {
          console.error("Error fetching user data for navigation:", error);
          navigate("/issuer/tenant");
        }
      };
      fetchAndNavigate();
    }
  };

  const public_navs = [
    authContext.isLoggedIn
      ? {
          name: "Dashboard",
          to: "/",
          match: {
            type: "exact",
            path: "/",
          },
          icon: HomeIcon,
        }
      : {
          name: "Templates",
          to: "/discovery",
          match: {
            type: "exact",
            path: "/discovery",
          },
          icon: Template,
        },
  ];

  const all_navs = [
    {
      name: "Dashboard",
      to: authContext.isLoggedIn ? "/issuer/discover" : "/",
      match: {
        type: "partial",
        path: authContext.isLoggedIn
          ? authContext.userType?.toLowerCase() === "programme_approver" ||
            authContext.userType?.toLowerCase() === "approver"
            ? "/issuer/discover/approvals"
            : "/issuer/discover"
          : "/",
      },
      icon: HomeIcon,
    },
    {
      name: "Designs",
      to: "/discovery",
      match: {
        type: "exact",
        path: "/discovery",
      },
      icon: Template,
    },
    {
      name: "Workspace",
      to: "/issuer/workspace",
      match: {
        type: "partial",
        path: "/issuer/workspace",
      },
      icon: WorkspaceIcon,
      disabledIcons: FolderFavoriteDisabled,
      onClick: handleWorkSpaceClick,
    },
    ...(authContext.sidebarComponents.includes("Draft")
      ? [
          {
            name: "Draft",
            to: "/issuer/drafts",
            match: {
              type: "partial",
              path: "/issuer/drafts",
            },
            icon: DraftsIcon,
            disabledIcons: FolderFavoriteDisabled,
            onClick: handleDraftsClick,
          },
        ]
      : []),
    {
      name: "History",
      to: "/issuer/transaction/all",
      match: {
        type: "partial",
        path: "/issuer/transaction/",
      },
      icon: Issued,
      disabledIcons: IssuedDisabled,
      onClick: handleIssuedClick,
    },
    ...(authContext.sidebarComponents.includes("Approvals")
      ? [
          {
            name: "Approvals",
            to: "/issuer/approvals/all",
            match: {
              type: "partial",
              path: "/issuer/approvals",
            },
            icon: ApprovalIcon,
          },
        ]
      : []),

    ...(authContext.sidebarComponents.includes("Organisation")
      ? [
          {
            name: "Tenant",
            apiName: "Organisation",
            to: authContext.tenantCode
              ? `/issuer/tenant/${authContext.tenantCode}/organisations/users`
              : "/issuer/tenant",
            match: {
              type: "partial",
              path: "/issuer/tenant/",
            },
            icon: TenantIcon,
            disabledIcons: TenantIconDisabled,
            onClick: handleOrganisationClick,
          },
        ]
      : []),

    ...(authContext.sidebarComponents.includes("Verifier Approvals")
      ? [
          {
            name: "Verifier Approvals",
            to: "/issuer/verifier-approvals",
            match: {
              type: "partial",
              path: "/issuer/verifier-approvals",
            },
            icon: ApprovalIcon,
          },
        ]
      : []),
    ...(authContext.sidebarComponents.includes("Tenant")
      ? [
          {
            name: "Tenant",
            apiName: "Tenant",
            to: "/issuer/tenant",
            match: {
              type: "exact",
              path: "/issuer/tenant",
            },
            icon: TenantIcon,
            disabledIcons: TenantIconDisabled,
          },
        ]
      : []),
    {
      name: "Settings",
      to: "/issuer/settings",
      match: {
        type: "partial",
        path: "/issuer/settings",
      },
      icon: Settings,
      disabledIcons: SettingsDisabled,
    },
  ];

  const shouldMatchPath = (nav) => {
    const { match } = nav;

    if (match.type === "exact") {
      return location.pathname === match.path;
    }

    if (match.type === "partial") {
      return location.pathname.startsWith(match.path);
    }

    return false;
  };

  const filteredNavs = authContext.isLoggedIn
    ? all_navs.filter(
        (nav) =>
          nav.name !== "Settings" &&
          (nav.apiName
            ? authContext.sidebarComponents.includes(nav.apiName)
            : authContext.sidebarComponents.includes(nav.name))
      )
    : public_navs;

  return (
    <div
      className="flex sm:h-full h-[4rem] bg-cfSecondary items-center sm:items-start 
        sm:w-[5.688rem] w-full border-t-[1.5px] sm:border-t-0 sm:border-r-[1.5px] fixed sm:relative bottom-0 overflow-x-clip overflow-y-clip"
    >
      <div className="flex w-full">
        <div className="flex flex-row sm:!flex-col h-full w-full font-CfSecondaryFont gap-y-4 gap-x-0 justify-start text-center">
          <div className="flex w-full">
            <div className="flex flex-row sm:!flex-col h-full w-full font-CfSecondaryFont gap-y-4 gap-x-0 justify-start">
              {(authContext.isLoggedIn ? filteredNavs : public_navs).map(
                ({ name, to, match, icon, onClick, apiName }) => (
                  <NavLink
                    key={apiName || name}
                    to={to}
                    title={name}
                    className="flex flex-grow justify-center w-[20%] sm:w-full font-CfPrimaryFont text-sm capitalize py-2 bg-opacity-40 hover:text-cfPrimaryLight hover:no-underline"
                    onClick={onClick}
                  >
                    <div
                      className={`h-1 sm:h-14 w-[20%] sm:w-1 absolute bottom-0 sm:bottom-auto sm:left-0 transition-all duration-50 ease-in-out ${
                        shouldMatchPath({ name, to, match }) &&
                        "bg-cfPrimaryCardHover"
                      }`}
                    />
                    <div
                      className={`w-full flex flex-col items-center gap-1 sm:gap-1 justify-center ${
                        shouldMatchPath({ name, to, match }) && "font-semibold"
                      }`}
                    >
                      <img src={icon} alt="" className="w-7 h-8 sm:w-6" />
                      <div
                        className="text-xs sm:text-sm overflow-hidden text-ellipsis"
                        style={{ maxWidth: "90%" }}
                      >
                        {name}
                      </div>
                    </div>
                  </NavLink>
                )
              )}
              {authContext.isLoggedIn &&
                authContext.sidebarComponents.includes("Settings") && (
                  <NavLink
                    to="/issuer/settings"
                    title="Settings"
                    className="sm:hidden flex flex-grow justify-center w-[20%] font-CfPrimaryFont text-sm capitalize py-2 bg-opacity-40 hover:text-cfPrimaryLight hover:no-underline"
                  >
                    <div
                      className={`h-1 w-[20%] absolute bottom-0 transition-all duration-50 ease-in-out ${
                        location.pathname.startsWith("/issuer/settings") &&
                        "bg-cfPrimaryCardHover"
                      }`}
                    />
                    <div
                      className={`w-full flex flex-col items-center gap-1 justify-center ${
                        location.pathname.startsWith("/issuer/settings") &&
                        "font-semibold"
                      }`}
                    >
                      <img src={Settings} alt="" className="w-7 h-8" />
                      <div
                        className="text-xs overflow-hidden text-ellipsis"
                        style={{ maxWidth: "90%" }}
                      >
                        Settings
                      </div>
                    </div>
                  </NavLink>
                )}
            </div>
          </div>
        </div>
      </div>

      {authContext.isLoggedIn &&
        authContext.sidebarComponents.includes("Settings") && (
          <div className="absolute bottom-20 w-full">
            <NavLink
              to="/issuer/settings"
              title="Settings"
              className="flex flex-grow justify-center font-CfPrimaryFont text-sm capitalize bg-opacity-40 pb-1 hover:text-cfPrimaryLight hover:no-underline"
            >
              <div
                className={`h-1 sm:h-14 w-20 sm:w-1 absolute bottom-0 sm:bottom-auto sm:left-0 ${
                  location.pathname.startsWith("/issuer/settings") &&
                  "bg-cfPrimaryCardHover"
                }`}
              />
              <div
                className={`w-full flex flex-col items-center gap-1 justify-center ${
                  location.pathname.startsWith("/issuer/settings") &&
                  "font-semibold"
                }`}
              >
                <img src={Settings} alt="" className="w-6" />
                <div className="text-xs sm:text-sm">Settings</div>
              </div>
            </NavLink>
          </div>
        )}
    </div>
  );
};

export default Sidebar;
